import http from "./http";

export enum StationRequestStatus {
	Pending = 1,
	Accepted = 2,
	Denied = 3,
	Draft = 4,
}

/**
 * Trạng thái NHÓM 1 do máy chủ tính, gắn kèm mỗi hồ sơ ở `GET /forms`.
 *
 * Bản thiết kế anh Quân duyệt 27/08/2026 (mục 4.3) chốt năm trạng thái, đều nghĩa
 * "chưa xong": Draft · Need Approval · Awaiting Signature · Expired · Declined.
 * Ký xong thì hồ sơ **không còn trạng thái riêng** — máy chủ trả `nhom: 2` để màn này
 * đưa nó ra khỏi hàng đợi, vì lúc đó nó đã là station.
 *
 * KHÔNG tự tính lại ở đây: hồ sơ luồng cũ không mang dấu vết nào cho biết đã thành
 * station hay chưa, chỉ máy chủ tra `Partners` mới biết.
 */
export interface TrangThaiHoSo {
	ma: string;
	nhan: string;
	nhom: 1 | 2;
}

export enum StationRequestType {
	Register = 1,
	Contact = 2,
}

export interface StationRequestRecord {
	id: number;
	email: string;
	status: number;
	type: number;
	denyReason?: string | null;
	createdAt?: string;
	updatedAt?: string;
	catalystCallAttempts?: number | null;
	catalystLastCallAt?: string | null;
	catalystNote?: string | null;
	partnerInfo?: Record<string, any>;
	contractInfo?: Record<string, any>;
	extenalInfo?: Record<string, any>;
	/** Máy chủ tính sẵn — xem `TrangThaiHoSo`. Hồ sơ cũ chưa có thì rơi về `status`. */
	trangThaiHoSo?: TrangThaiHoSo;
}

export interface StationRequestDestinationOption {
	id: string;
	name: string;
	country: string | null;
}

export async function getStationRequests(
	page = 1,
	perPage = 200,
	status?: StationRequestStatus | null,
	type: StationRequestType = StationRequestType.Register,
	/**
	 * Chỉ lấy hồ sơ NHÓM 1 (chưa thành station). Máy chủ lọc trước khi chia trang —
	 * nếu để trình duyệt tự lọc thì mỗi trang rơi rụng một số dòng khác nhau và hàng
	 * đợi báo một đằng, danh sách hiện một nẻo (đo dev 30/08: thẻ 15, danh sách 13).
	 */
	chiNhomMot = false
) {
	const params: Record<string, any> = {
		page,
		perPage,
		type,
	};

	if (chiNhomMot) {
		params.chiNhomMot = 1;
	}

	if (status) {
		params.status = status;
	}

	const { data } = await http.get("/forms", {
		params,
	});

	return {
		docs: (data?.docs || []) as StationRequestRecord[],
		count: data?.total || data?.totalDocs || data?.count || 0,
		pages: data?.pages || 0,
		currentPage: data?.currentPage || page,
	};
}

export async function approveStationRequest(id: number) {
	return http.post(`/accept-form/${id}`, {});
}

/** Same handler as approve — confirms station after catalyst call (Instant Go-Live). */
export async function verifyStationGoLive(id: number) {
	return http.post(`/forms/${id}/verify-golive`, {});
}

export async function logStationRequestCallAttempt(id: number, note?: string) {
	return http.post(`/forms/${id}/catalyst-call`, { note: note || "" });
}

export async function denyStationRequest(id: number, reason: string) {
	return http.post(`/denied-form/${id}`, { reason });
}

export async function updateStationRequest(
	id: number,
	payload: {
		email: string;
		partnerInfo: Record<string, any>;
		contractInfo: Record<string, any>;
		extenalInfo?: Record<string, any>;
	}
) {
	return http.put(`/forms/${id}`, payload);
}

export async function getStationRequestDestinations(
	search = ""
): Promise<StationRequestDestinationOption[]> {
	const { data } = await http.get(`/destination-search?search=${encodeURIComponent(search)}`);
	return Array.isArray(data) ? data : [];
}

/** Same persistence as duyệt station request: tạo slug destination nếu chưa có (admin JWT). */
export async function adminEnsureDestination(
	city: string,
	country: string,
	coords?: { lat?: number; long?: number }
): Promise<{ destinationId: string }> {
	const payload: Record<string, unknown> = {
		city: String(city || "").trim(),
		country: String(country || "").trim(),
	};
	if (coords?.lat != null && isFinite(coords.lat)) payload.lat = coords.lat;
	if (coords?.long != null && isFinite(coords.long)) payload.long = coords.long;
	const { data } = await http.post(`/utility/admin-ensure-destination`, payload);
	return data;
}

/**
 * Cấp lại mật khẩu tạm cho hồ sơ đã duyệt và gia hạn thêm 7 ngày.
 *
 * Luồng `ONBOARDING_V2`: tài khoản trạm sinh ra ở bước duyệt kèm mật khẩu tạm hạn 7
 * ngày. Hết hạn — hoặc đối tác không tìm thấy thư — thì không có đường nào vào cổng
 * nữa; nút này là đường đó. Mật khẩu cũ chết ngay khi mật khẩu mới được ghi.
 */
export async function guiLaiMatKhauTam(formId: number) {
	return http.post(`/forms/${formId}/gui-lai-mat-khau-tam`, {});
}

/**
 * Ghi danh sách ĐIỂM của một hồ sơ chuỗi.
 *
 * Không dùng `updateStationRequest` vì `PUT /forms/:id` đòi hồ sơ có địa chỉ, link Maps,
 * quốc gia và giờ mở cửa — hồ sơ chuỗi không có, địa chỉ nằm ở từng điểm. Route riêng
 * chỉ chạm đúng nhánh `extenalInfo.multiLocation.locations`.
 */
export async function luuDiemChuoi(formId: number, locations: Record<string, string>[]) {
	return http.put(`/forms/${formId}/chain-locations`, { locations });
}

/**
 * Giải link Google Maps -> tên/địa chỉ/toạ độ/điện thoại (và nước/thành phố khi backend
 * trả). Admin dán link vào ô "Google Maps link" của 1 điểm chuỗi rồi bấm "Trích từ Maps"
 * để tự điền form, sau đó rà lại. Tái dùng endpoint public của station portal.
 */
export interface GiaiLinkMapsResult {
	ok?: boolean;
	ten?: string;
	diaChi?: string;
	lat?: number | string | null;
	long?: number | string | null;
	soDienThoai?: string;
	nuoc?: string;
	thanhPho?: string;
	urlDayDu?: string;
	canhBao?: string;
}
export async function giaiLinkGoogleMapsChoDiem(link: string) {
	const { data } = await http.get<GiaiLinkMapsResult>("/giai-link-google-maps", {
		params: { link },
	});
	return data;
}

/**
 * Tải ảnh khu gửi đồ cho một hồ sơ đăng ký.
 *
 * Vì sao Admin cần tự tải: từ 30/08 hồ sơ phải ĐỦ mới bấm Approve được, mà ảnh là một
 * mục bắt buộc theo Sheet. Hồ sơ nộp qua form cũ không có ảnh nào — không có chỗ tải
 * thì Admin không bao giờ duyệt được chúng.
 *
 * Dùng lại đúng đường `gcs-upload-multiple-file` mà màn đối soát đang dùng, không mở
 * thêm endpoint nào.
 */
export async function uploadStationRequestFiles(files: File[]): Promise<string[]> {
	if (!files || files.length === 0) {
		return [];
	}

	const formData = new FormData();
	files.forEach((file) => formData.append("files", file));

	const { data } = await http.post("/gcs-upload-multiple-file?folder=documents/station-requests", formData, {
		headers: { "Content-Type": "multipart/form-data" },
	});

	return (data || []).map((item: any) => item.path || item.url || item.filename).filter(Boolean);
}
